import { useState } from 'react';
import { DateTime } from 'luxon';
import { CalendarPlus, Pencil, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { specialDayStatus } from '@/lib/bot-service-hours';
import { SpecialDayDialog } from './special-day-dialog';

// Date-exception list: rows sorted by date with a status pill (today amber, past dimmed and
// collapsed behind a "Show N past days" toggle). Edit and delete mutate the DRAFT — the save
// bar commits them together with everything else.

/** "All numbers" / one label / "N numbers" chip with the full labels in the title attr. */
export function ScopeChip({ gatewayBotIds, gateways }) {
  if (gatewayBotIds == null) {
    return (
      <span className="shrink-0 rounded-full border border-border px-2 py-0.5 text-[10.5px] text-muted-foreground">
        All numbers
      </span>
    );
  }
  const labels = gatewayBotIds.map((id) => gateways.find((g) => g.botId === id)?.label || id);
  const text = gatewayBotIds.length === 1 ? labels[0] : `${gatewayBotIds.length} numbers`;
  return (
    <span
      title={labels.join(', ')}
      className="shrink-0 rounded-full border border-border px-2 py-0.5 text-[10.5px] text-muted-foreground"
    >
      {text}
    </span>
  );
}

function StatusPill({ status }) {
  if (status === 'active') {
    return (
      <span className="shrink-0 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10.5px] font-semibold text-amber-700 dark:border-amber-500/40 dark:bg-amber-950/60 dark:text-amber-300">
        Today
      </span>
    );
  }
  if (status === 'past') {
    return (
      <span className="shrink-0 rounded-full border border-border px-2 py-0.5 text-[10.5px] text-muted-foreground">
        Past
      </span>
    );
  }
  return (
    <span className="shrink-0 rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[10.5px] text-muted-foreground">
      Upcoming
    </span>
  );
}

function windowsText(windows) {
  return windows.map((window) => `${window.start}–${window.end}`).join(' & ');
}

function DayRow({ day, status, gateways, errors, disabled, onEdit, onRemove }) {
  const rowError = ['label', 'date', 'scope', 'windows', 'message']
    .map((field) => errors[`specialDays.${day.id}.${field}`])
    .filter(Boolean)
    .join(' ');
  const dateText = DateTime.fromISO(day.date).isValid
    ? DateTime.fromISO(day.date).toFormat('ccc d LLL yyyy')
    : day.date || '—';
  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-lg border bg-card px-3 py-2.5',
        rowError ? 'border-destructive/50' : 'border-border',
        status === 'past' && 'opacity-60',
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[13px] font-semibold text-foreground">{dateText}</span>
          <span className="truncate text-[13px] text-foreground">{day.label}</span>
          <ScopeChip gatewayBotIds={day.gatewayBotIds} gateways={gateways} />
          <StatusPill status={status} />
        </div>
        <p className="text-[11px] text-muted-foreground">
          {day.closed ? 'Closed all day' : `Open ${windowsText(day.windows)}`}
          {day.message ? ' · custom message' : ''}
        </p>
        {rowError ? <p className="text-[11px] text-destructive">{rowError}</p> : null}
      </div>
      {!disabled ? (
        <>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label={`Edit ${day.label}`}
            onClick={onEdit}
          >
            <Pencil className="size-3.5 text-muted-foreground" aria-hidden />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label={`Delete ${day.label}`}
            onClick={onRemove}
          >
            <Trash2 className="size-3.5 text-muted-foreground" aria-hidden />
          </Button>
        </>
      ) : null}
    </div>
  );
}

export function SpecialDaysSection({
  days = [],
  gateways = [],
  placeholders = [],
  errors = {},
  disabled = false,
  nowISO,
  onUpsert,
  onRemove,
}) {
  const [dialog, setDialog] = useState(null); // { day: object|null } while open
  const [showPast, setShowPast] = useState(false);

  const sorted = [...days].sort((a, b) =>
    a.date === b.date ? (a.id < b.id ? -1 : 1) : a.date < b.date ? -1 : 1,
  );
  const withStatus = sorted.map((day) => ({ day, status: specialDayStatus(day, nowISO) }));
  const past = withStatus.filter((entry) => entry.status === 'past');
  const upcoming = withStatus.filter((entry) => entry.status !== 'past');

  return (
    <div className="flex flex-col gap-1.5">
      {upcoming.length === 0 && past.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border px-4 py-5 text-center text-xs text-muted-foreground">
          No special days yet — the weekly hours apply every day.
        </p>
      ) : null}
      {upcoming.map(({ day, status }) => (
        <DayRow
          key={day.id}
          day={day}
          status={status}
          gateways={gateways}
          errors={errors}
          disabled={disabled}
          onEdit={() => setDialog({ day })}
          onRemove={() => onRemove(day.id)}
        />
      ))}
      {past.length > 0 ? (
        <button
          type="button"
          className="self-start px-1 py-0.5 text-[11px] text-muted-foreground transition hover:text-foreground"
          onClick={() => setShowPast((current) => !current)}
        >
          {showPast ? 'Hide past days' : `Show ${past.length} past day${past.length === 1 ? '' : 's'}`}
        </button>
      ) : null}
      {showPast
        ? past.map(({ day, status }) => (
            <DayRow
              key={day.id}
              day={day}
              status={status}
              gateways={gateways}
              errors={errors}
              disabled={disabled}
              onEdit={() => setDialog({ day })}
              onRemove={() => onRemove(day.id)}
            />
          ))
        : null}
      {!disabled ? (
        <div>
          <Button type="button" variant="outline" size="sm" onClick={() => setDialog({ day: null })}>
            <CalendarPlus className="mr-1.5 size-4" aria-hidden />
            Add special day
          </Button>
        </div>
      ) : null}
      <SpecialDayDialog
        open={Boolean(dialog)}
        onOpenChange={(open) => {
          if (!open) setDialog(null);
        }}
        day={dialog?.day || null}
        gateways={gateways}
        placeholders={placeholders}
        onSave={onUpsert}
      />
    </div>
  );
}
